import { FC, useEffect, useState, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import Content, { ContentNS } from "content/content";
import Logging from "logging";
import { useAppStore } from "store/store";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import SessionIneligibilityReason, {
  SessionIneligibilityReasonFromApi,
} from "models/session-ineligibility-reason";
import {
  PaxCheckAnswer,
  PaxCheckAnswerFromJSON,
  PaxCheckDeclarationItem,
  PaxCheckHaveDocumentQuestion,
  PaxCheckPickFromListQuestion,
} from "api";

/* hooks */
import { getGeneralAnalyticsParams } from "features/shared/hooks/analytics/useGeneralAnalytics";
import useMutatedPagePath from "features/shared/hooks/analytics/useMutatedPagePath";
import { getNavigateToDeclarations } from "features/shared/hooks/navigation/useFlowRedirect";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import usePaxLevelAnalytics, {
  getPaxLevelParams,
} from "features/shared/hooks/analytics/usePaxLevelAnalytics";
import useNavigateBackInfo, {
  PageType,
} from "features/shared/hooks/navigation/useNavigateBackInfo";

/* services */
import AnalyticsService from "services/analytics/analytics";
import DataProvider, {
  SubmitAnswersRequest,
} from "services/data-provider/data-provider";

/* components */
import LinkButton from "features/shared/components/buttons/link-button/LinkButton";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import LocalLink from "features/shared/components/link/LocalLink";
import PickFromListQuestion from "./components/pick-from-list-question/PickFromListQuestion";
import YesNoQuestionsPane from "./components/yes-no-questions/YesNoQuestionsPane";
import HaveDocumentQuestionsPane from "./components/have-document-questions/HaveDocumentQuestionsPane";

/* styles */
import Body from "features/shared/styles/typography/Body.styled";
import { TertiaryHeader } from "features/shared/styles/typography/TertiaryHeader.styled";
import FormButtons from "./styles/FormButtons.styled";
import StyledQuestionsForm from "./styles/StyledQuestionsForm.styled";
import QuestionsCard from "./styles/QuestionsCard.styled";

interface NavigationState {
  // whether user comes back to questions page to edit answers
  // vs first time landing on the page. default is false
  isEditing?: boolean;
  // show only "have document" questions, default is false
  showOnlyHaveDocumentQuestions?: boolean;
  // show only "declaration" questions, default is false
  isDeclaration?: boolean;
}

type LocalErrors = { [key: string]: string | null };

interface QuestionsSubmitProps {
  setAllHaveDocumentQuestionsTo?: boolean;
}

function getPageType(
  isDeclaration: boolean,
  showOnlyHaveDocumentQuestions: boolean
): PageType {
  if (isDeclaration) {
    return PageType.QuestionsDeclarations;
  }

  if (showOnlyHaveDocumentQuestions) {
    return PageType.QuestionsHaveDocument;
  }

  return PageType.QuestionsAll;
}

/*
Can work in several modes:
1. show all questions:
    showOnlyHaveDocumentQuestions is false
    isDeclaration is false

    isEditing=true adds "Cancel" button that leads back to checklist

2. show only "have document" questions (new flow):
    showOnlyHaveDocumentQuestions is true
    isDeclaration is false

3. show "declaration" questions (new flow):
    showOnlyHaveDocumentQuestions is false
    isDeclaration is true

    [!] it is assumed that by "declaration" stage there are none not answered
    "have document" or "pick-from-list" questions, so it is only for cases with
    ETAs (ie haven't been to certain countries, have electronic passport, etc),
    or visit duration. Later on, the plan is to get rid of these and make them a
    part of InfoItem.
*/
const QuestionsPage: FC = () => {
  const locale = useAppStore((state) => state.locale);

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSessions = useAppStore((state) => state.paxSessions);

  const journey = useAppStore((state) => state.journey);

  const [path, title, locationPath] = useMutatedPagePath();

  const analyticsSendPageView = usePaxLevelAnalytics();

  const paxSession = usePaxSession();
  if (!paxSession) {
    throw new Error("invalid paxSession");
  }

  const paxSessionId = paxSession?.paxSessionId;

  const navigate = useNavigateWithQuery();

  const textChecklist = (s: string) =>
    Content.shared.t(s, { ns: ContentNS.checklist });
  const direction = Content.shared.instance.dir();

  const [updatePaxSession] = useAppStore(
    useShallow((state) => [state.updatePaxSession])
  );

  const setIneligibilityReason = useAppStore(
    (state) => state.setIneligibilityReason
  );

  const setSessionIsLocked = useAppStore((state) => state.setSessionIsLocked);

  const location = useLocation();
  const locationState = location.state
    ? (location.state as NavigationState)
    : undefined;

  const [isLoading, setIsLoading] = useState(false);

  // define what questions to show
  let isEditing = false;
  let showOnlyHaveDocumentQuestions = false;
  let isDeclaration = false;

  if (locationState) {
    isEditing = locationState.isEditing || false;
    showOnlyHaveDocumentQuestions =
      locationState.showOnlyHaveDocumentQuestions || false;
    isDeclaration = locationState.isDeclaration || false;
  }

  const backLinkInfo = useNavigateBackInfo(
    getPageType(isDeclaration, showOnlyHaveDocumentQuestions)
  );

  const pageTitle = showOnlyHaveDocumentQuestions
    ? textChecklist("questions.have-document.title")
    : textChecklist("questions.declarations.title");
  const pageSubtitle = showOnlyHaveDocumentQuestions
    ? textChecklist("questions.have-document.subtitle")
    : textChecklist("questions.declarations.subtitle");

  // clients may override the CTA for the have-document step only
  const submitButtonTitle = showOnlyHaveDocumentQuestions
    ? Content.shared.t("questions.have-document.submit-button", {
        ns: ContentNS.checklist,
        defaultValue: textChecklist("questions.submit-button"),
      })
    : textChecklist("questions.submit-button");

  const haveDocumentQuestions = useMemo(
    () =>
      !isDeclaration
        ? paxSession.checklist.questions.filter(
            (question) => question.type === "have-document-question"
          ) || []
        : [],
    [isDeclaration, paxSession.checklist.questions]
  );

  const pickFromListQuestions = useMemo(
    () =>
      !showOnlyHaveDocumentQuestions
        ? paxSession.checklist.questions.filter(
            (question) => question.type === "pick-from-list-question"
          ) || []
        : [],
    [showOnlyHaveDocumentQuestions, paxSession.checklist.questions]
  );

  // for declarations, we only show what is relevant
  const declarationQuestions = useMemo(
    () =>
      !showOnlyHaveDocumentQuestions
        ? paxSession.checklist.questions.filter(
            (question) =>
              question.type === "declaration-item" &&
              (question.isRelevant || (isEditing && question.answer !== undefined))
          ) || []
        : [],
    [showOnlyHaveDocumentQuestions, paxSession.checklist.questions, isEditing]
  );

  // questions to present on page and work with
  const questionsToPresent = useMemo(
    () => [
      ...haveDocumentQuestions,
      ...declarationQuestions,
      ...pickFromListQuestions,
    ],
    [haveDocumentQuestions, declarationQuestions, pickFromListQuestions]
  );

  const [errors, setErrors] = useState<LocalErrors>({});

  const analyticsParams = {
    ...getGeneralAnalyticsParams(
      paxSessions,
      journey,
      path,
      title,
      locationPath,
      locale
    ),
    ...getPaxLevelParams(paxSessions, paxSession?.paxSessionId || null),
  };

  // answers state
  const [answersByQuestionId, setAnswers] = useState<
    Map<string, boolean | string | undefined>
  >(new Map());

  useEffect(() => {
    if (answersByQuestionId.size === 0 && questionsToPresent.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAnswers(
        new Map(
          questionsToPresent.map((q) => {
            let defaultValue: boolean | string | undefined = false; // false by default (have doc question)
            if (q.type === "pick-from-list-question") {
              // pick from list question - get first option as default value
              const qType = q as PaxCheckPickFromListQuestion;
              defaultValue =
                qType.defaultValue !== null ? qType.defaultValue : undefined;
            }
            if (q.type === "declaration-item") {
              defaultValue = q.defaultValue;
            }

            return [
              q.id,
              q.answer !== undefined && q.answer !== null
                ? q.answer
                : defaultValue,
            ];
          })
        )
      );
      setErrors(() => {
        const e: LocalErrors = {};
        questionsToPresent.forEach((q) => {
          e[q.id] = null;
        });
        return e;
      });
    }
  }, [questionsToPresent, answersByQuestionId.size]);

  const handleQuestionAnswerChange =
    (id: string) => (value: boolean | string) => {
      setAnswers(new Map(answersByQuestionId).set(id, value));
      setErrors({ ...errors, [id]: null });
    };

  useEffect(() => {
    analyticsSendPageView();

    AnalyticsService.shared.trackEvent(
      "questions_presented",
      {
        ...analyticsParams,
        questions_number: questionsToPresent.length,
      },
      false // isScanRelated
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (questionsToPresent.length === 0) {
    // there are no questions to present for this checklist
    return <Navigate to="../checklist" />;
  }

  return (
    <StyledQuestionsForm>
      {backLinkInfo ? (
        <LocalLink to={backLinkInfo.to} state={backLinkInfo.state} backLink>
          {backLinkInfo.title}
        </LocalLink>
      ) : null}

      <TertiaryHeader>{pageTitle}</TertiaryHeader>
      {pageSubtitle !== "" ? <Body>{pageSubtitle}</Body> : null}

      <QuestionsCard>
        <div>
          {haveDocumentQuestions.length > 0 ? (
            <HaveDocumentQuestionsPane
              questions={
                haveDocumentQuestions as PaxCheckHaveDocumentQuestion[]
              }
              answers={answersByQuestionId}
              handleChange={handleQuestionAnswerChange}
            />
          ) : null}

          {pickFromListQuestions.map((q) => {
            const question = q as PaxCheckPickFromListQuestion;
            const defaultValue =
              question.defaultValue !== null
                ? question.defaultValue
                : undefined;
            return (
              <PickFromListQuestion
                key={q.id}
                question={question}
                error={errors[q.id]}
                answer={answersByQuestionId.get(q.id) || defaultValue}
                options={question.options}
                handleChange={handleQuestionAnswerChange}
              />
            );
          })}

          {declarationQuestions.length > 0 ? (
            <YesNoQuestionsPane
              questions={declarationQuestions as PaxCheckDeclarationItem[]}
              answers={answersByQuestionId}
              handleChange={handleQuestionAnswerChange}
            />
          ) : null}
        </div>
        <FormButtons direction={direction} displayInline={false}>
          <SubmitButton onClick={() => onFormSubmit()} isLoading={isLoading}>
            {submitButtonTitle}
          </SubmitButton>

          {showOnlyHaveDocumentQuestions && (
            <div>
              <LinkButton
                onClick={() =>
                  onFormSubmit({ setAllHaveDocumentQuestionsTo: false })
                }
              >
                {textChecklist("questions.none-of-above")}
              </LinkButton>
            </div>
          )}
        </FormButtons>
      </QuestionsCard>
    </StyledQuestionsForm>
  );

  function validateForm(): boolean {
    const newErrors: LocalErrors = {};
    let formValid = true;

    questionsToPresent.forEach((q) => {
      const value = answersByQuestionId.get(q.id);

      switch (q.type) {
        case "declaration-item":
          break;

        case "have-document-question":
          break;

        case "pick-from-list-question": {
          const question = q as PaxCheckPickFromListQuestion;
          if (value === undefined && question.defaultValue === undefined) {
            newErrors[q.id] = Content.shared.t("question.answer-required");
            formValid = false;
          }
          break;
        }
      }
    });

    setErrors(newErrors);

    return formValid;
  }

  async function sendAnswers(
    callback: () => any,
    request?: SubmitAnswersRequest
  ) {
    try {
      const provider = DataProvider.shared;
      const updatedPaxSession = await provider.submitAnswers(
        sessionId,
        paxSessionId,
        request
      );

      // success

      let questionsNumber = 0;
      if (request?.nationality) {
        questionsNumber += 1;
      }
      if (request?.birthdate) {
        questionsNumber += 1;
      }
      if (request?.answers) {
        questionsNumber += request.answers.length;
      }
      if (request?.confirmDeclarations) {
        questionsNumber += request.confirmDeclarations.length;
      }
      if (request?.refuteDeclarations) {
        questionsNumber += request.refuteDeclarations.length;
      }

      AnalyticsService.shared.trackEvent(
        "questions_answered",
        {
          ...analyticsParams,
          questions_number: questionsNumber,
        },
        false // isScanRelated
      );

      updatePaxSession(updatedPaxSession);
      callback();
    } catch (err) {
      setIsLoading(false);

      if (
        err instanceof DataProviderGeneralException &&
        err.type !== DataProviderExceptionType.Unknown &&
        err.type !== DataProviderExceptionType.BadRequest &&
        err.type !== DataProviderExceptionType.NotFound
      ) {
        // api "general" error
        switch (err.type) {
          case DataProviderExceptionType.NotProcessable:
          case DataProviderExceptionType.NotEligible:
            // not processable or not eligible
            setIneligibilityReason(
              SessionIneligibilityReasonFromApi(err.message)
            );
            return;

          case DataProviderExceptionType.SessionIsFinalised:
            setIneligibilityReason(
              SessionIneligibilityReason.SessionIsFinalised
            );
            return;

          case DataProviderExceptionType.SessionIsLocked:
            setSessionIsLocked(true);
            return;
        }
      }

      Logging.log(
        "questions",
        paxSessionId,
        `Failed to submit answers: ${err instanceof Error ? err.message : err}`
      );
    }
  }

  async function onFormSubmit(props?: QuestionsSubmitProps) {
    if (isLoading) {
      return;
    }

    if (!(paxSession?.checklist && sessionId)) {
      Logging.log("questions", paxSessionId, "Missing checklist or session");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const answers: PaxCheckAnswer[] = [];
    const confirmDeclarations: string[] = [];
    const refuteDeclarations: string[] = [];

    questionsToPresent.forEach((q) => {
      const value = answersByQuestionId.get(q.id);

      switch (q.type) {
        case "declaration-item":
          if (value === false) {
            refuteDeclarations.push(q.id);
            break;
          }

          // inverted logic - for declarations, default is checked
          confirmDeclarations.push(q.id);
          break;

        case "have-document-question": {
          const defaultValue = value !== undefined ? value : false;
          const valueToSend =
            props?.setAllHaveDocumentQuestionsTo !== undefined
              ? props.setAllHaveDocumentQuestionsTo // overwrite (ie "None of above" option)
              : defaultValue;

          answers.push(
            PaxCheckAnswerFromJSON({
              type: "bool-answer",
              question_id: q.id,
              value: valueToSend,
            })
          );
          break;
        }

        case "pick-from-list-question": {
          const question = q as PaxCheckPickFromListQuestion;
          answers.push(
            PaxCheckAnswerFromJSON({
              type: "from-list-answer",
              question_id: q.id,
              value: value ? (value as string) : question.defaultValue,
            })
          );
          break;
        }
      }
    });

    sendAnswers(
      () => {
        // once all answers are submitted
        let defaultRedirect = true;
        if (isEditing) {
          // if editing and new flow - need to check if any declaration items
          // were answered in the session. If there is at least 1 answered declaration
          // item, it means that "declaration" have been shown before - so we should navigate there
          const anyAnsweredDeclarationItems =
            paxSession.checklist.questions.filter(
              (q) =>
                // only deal with relevant questions for declarations
                (q.isRelevant || q.answer !== undefined) &&
                (q.type === "declaration-item" ||
                  q.type === "pick-from-list-question")
            );

          if (anyAnsweredDeclarationItems.length > 0) {
            defaultRedirect = false;
            const [url, state] = getNavigateToDeclarations(
              sessionId,
              paxSessionId,
              !isDeclaration
            );
            navigate(url, {
              state: state,
            });
            setIsLoading(false);
          }
        }

        if (defaultRedirect) {
          // default - redirect to /checklist for main flow navigation
          navigate("../checklist");
        }
      },
      {
        answers: answers,
        confirmDeclarations: confirmDeclarations,
        refuteDeclarations: refuteDeclarations,
      }
    );
  }
};

export default QuestionsPage;
